import { useContext, useCallback, useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { Typography, Grid, CircularProgress } from '@mui/material'
import { object } from 'prop-types'
import { CvsAutoComplete, CvsTextField, CvsFieldMapperV2 } from '@components'
import { connectorContext, modalContext } from '@contexts'
import { api } from 'api'
import {
  getMappableFields,
  transformIntegrationFieldMappings,
} from '../../../../../components/field-mapperV2/utils'
import { templateCopyContext } from '../context'

const FullWidth = styled.div`
  width: 100%;
`
const IntegrationDetailsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 15px;
`
const AccountConfigContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 15px;
`
const DataImportConfigContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 15px;
`

export const GoogleAnalytics = ({ assignment }) => {
  const GADatasetTypes = [
    {
      label: t`Custom Data`,
      value: 'Custom Data',
    },
    {
      label: t`Campaign Data`,
      value: 'Campaign Data',
    },
    {
      label: t`User Data`,
      value: 'User Data',
    },
    {
      label: t`Geography Data`,
      value: 'Geography Data',
    },
    {
      label: t`Content Data`,
      value: 'Content Data',
    },
    {
      label: t`Product Data`,
      value: 'Product Data',
    },
  ]

  const ModalContext = useContext(modalContext)
  const { template, onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const [loadingFields, setLoadingFields] = useState(true)
  const [fields, setFields] = useState([])
  const [gaAccounts, setGaAccounts] = useState([])
  const [gaProperties, setGaProperties] = useState([])
  const [gaCustomDataSources, setGaCustomDataSources] = useState([])

  useEffect(() => {
    api
      .get(`/api/v1/integrations/google-analytics/default-fields`)
      .then(resp => {
        setFields(resp.data)
        setLoadingFields(false)
      })
      .catch(() => {
        setLoadingFields(false)
      })
  }, [])

  useEffect(() => {
    fetchGAAccounts(assignment.connector_id)
    assignment.metadata?.ga_account_id && fetchProperties(assignment.metadata.ga_account_id)
    assignment.metadata?.ga_property_id &&
      fetchGaCustomDataSources(assignment.metadata.ga_property_id)
  }, [])

  const fieldOptions = useMemo(
    () =>
      Object.values(template.templateFieldsById).map(field => ({
        label: field.field.name,
        value: field.field?.id?.toString(),
      })),
    [template],
  )

  const onGAOutboundChange = useCallback(
    (value, name) => {
      onChange(currentOutbound => ({
        ...currentOutbound,
        [assignment.id]: {
          ...currentOutbound[assignment.id],
          [name]: value,
        },
      }))
    },
    [assignment, onChange],
  )

  const onGAIntegrationChange = useCallback(value => {
    onChange(currentIntegration => ({
      ...currentIntegration,
      [assignment.id]: {
        ...currentIntegration[assignment.id],
        ...value,
      },
    }))
  })

  const onGAMappingChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          mapping_fields: value,
        },
      }))
    },
    [assignment, onChange],
  )

  const connectors = useMemo(
    () =>
      Object.values(byId)
        .filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[assignment.connector_id].connector_type_id),
        )
        .map(({ name, id }) => ({
          label: name,
          value: id,
        })),
    [assignment, byId],
  )

  const fetchGAAccounts = account => {
    api.get(`/api/v1/account/connector/${account}/ga-account`).then(res => {
      setGaAccounts(res.data.gaAccounts)
    })
  }

  const fetchProperties = gaAccount => {
    api
      .get(
        `/api/v1/account/connector/${assignment.connector_id}/ga-account/${gaAccount}/property/list`,
      )
      .then(res => {
        setGaProperties(res.data.gaProperties)
      })
  }

  const fetchGaCustomDataSources = property => {
    api
      .get(
        `/api/v1/account/connector/${assignment.connector_id}/ga-account/${assignment.metadata.ga_account_id}/property/${property}/custom-data-source/list`,
      )
      .then(({ data }) => {
        setGaCustomDataSources(data.gaCustomDataSources)
      })
  }

  const confirmModal = item => {
    ModalContext.displayModal({
      title: t`Are you sure?`,
      description: t`Updating the associated dataset will reset any field mappings you have configured.`,
      confirm: () => () => {
        onGAIntegrationChange({
          mapping_fields: {},
          metadata: {
            ...assignment.metadata,
            ga_custom_data_source_id: item,
            schema: '',
          },
        })
      },
      cancel: () => {},
    })
  }

  return (
    <FullWidth>
      <Typography>{t`Integration details`}</Typography>
      <IntegrationDetailsContainer>
        <Grid xs={12}>
          <CvsTextField
            label={t`Integration name`}
            name="name"
            fullWidth
            onChange={({ target }) =>
              onChange(old => ({
                ...old,
                [assignment.id]: { ...old[assignment.id], name: target.value },
              }))
            }
            value={assignment.name}
          />
        </Grid>
      </IntegrationDetailsContainer>
      <Typography>{t`Account configuration`}</Typography>
      <AccountConfigContainer>
        <Grid item xs={3}>
          <CvsAutoComplete
            options={connectors}
            style={{ margin: 0 }}
            onSelected={selected => {
              onGAOutboundChange(selected, 'connector_id')
              fetchGAAccounts(selected)
            }}
            selected={assignment.connector_id}
            label={t`Connected Google Analytics account`}
          />
        </Grid>
        <Grid style={{ marginLeft: 25 }} item xs={3}>
          <CvsAutoComplete
            label={t`Google analytics account`}
            style={{ margin: 0 }}
            required={true}
            options={gaAccounts.map(t => ({
              value: t.ga_account_id,
              label: t.ga_account_name,
            }))}
            onSelected={item => {
              onGAOutboundChange({ ...assignment.metadata, ga_account_id: item }, 'metadata')
              fetchProperties(item)
            }}
            selected={assignment.metadata.ga_account_id}
            disabled={!assignment.connector_id}
          />
        </Grid>
        <Grid style={{ marginLeft: 25 }} item xs={3}>
          <CvsAutoComplete
            label={t`GA property`}
            style={{ margin: 0 }}
            required={true}
            options={gaProperties.map(t => ({
              value: t.ga_property_id,
              label: t.ga_property_name,
            }))}
            onSelected={item => {
              onGAOutboundChange({ ...assignment.metadata, ga_property_id: item }, 'metadata')
              fetchGaCustomDataSources(item)
            }}
            selected={assignment.metadata.ga_property_id}
            disabled={!assignment.metadata.ga_account_id}
            showSpinner={assignment.metadata.ga_property_id && !gaProperties.length}
          />
        </Grid>
      </AccountConfigContainer>
      <Typography>{t`Data import configuration`}</Typography>
      <DataImportConfigContainer>
        <Grid item xs={3}>
          <CvsAutoComplete
            label={t`Data set type`}
            style={{ margin: 0 }}
            options={GADatasetTypes}
            onSelected={item => {
              onGAOutboundChange({ ...assignment.metadata, ga_data_set_type: item }, 'metadata')
            }}
            selected={assignment.metadata.ga_data_set_type}
            required={true}
            showSpinner={
              assignment.metadata.ga_custom_data_source_id && !gaCustomDataSources.length
            }
          />
        </Grid>
        <Grid style={{ marginLeft: 25 }} item xs={3}>
          <CvsAutoComplete
            label={t`Data source`}
            style={{ margin: 0 }}
            options={gaCustomDataSources.map(t => ({
              value: t.ga_custom_data_source_id,
              label: t.ga_custom_data_source_name,
            }))}
            onSelected={item => {
              if (typeof assignment.metadata.ga_custom_data_source_id !== 'undefined') {
                confirmModal(item)
              } else
                onGAOutboundChange(
                  { ...assignment.metadata, ga_custom_data_source_id: item },
                  'metadata',
                )
            }}
            selected={assignment.metadata.ga_custom_data_source_id}
            disabled={!assignment.metadata.ga_property_id}
            required={true}
            showSpinner={
              assignment.metadata.ga_custom_data_source_id && !gaCustomDataSources.length
            }
          />
        </Grid>
      </DataImportConfigContainer>
      {loadingFields ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <CvsFieldMapperV2
          mappingType="outbound"
          integrationTitle="Google Analytics"
          integrationOptions={getMappableFields(fields, [])}
          templateOptions={fieldOptions}
          mappings={transformIntegrationFieldMappings(assignment.mapping_fields)}
          onSelect={value => onGAMappingChange(value)}
          onIntegrationChange={(schema, mappings) => {
            onGAIntegrationChange({
              metadata: {
                ...assignment.metadata,
                ...schema,
              },
              mapping_fields: mappings,
            })
          }}
          integration={assignment}
        />
      )}
    </FullWidth>
  )
}

GoogleAnalytics.propTypes = {
  assignment: object.isRequired,
}
